import React, { useState } from 'react';

const CarroAssinatura: React.FC = () => {
  const [valorCarro, setValorCarro] = useState('140000');
  const [mensalidadeAssinatura, setMensalidadeAssinatura] = useState('3290');
  const [prazoMeses, setPrazoMeses] = useState('36');
  const [taxaCdiAnual, setTaxaCdiAnual] = useState('12.5');
  const [depreciacaoAnual, setDepreciacaoAnual] = useState('12');

  const calcularComparativo = () => {
    const valor = parseFloat(valorCarro) || 0;
    const assinaturaMes = parseFloat(mensalidadeAssinatura) || 0;
    const meses = parseInt(prazoMeses, 10) || 36;
    const anos = meses / 12;
    const cdiAno = (parseFloat(taxaCdiAnual) || 0) / 100;
    const depAno = (parseFloat(depreciacaoAnual) || 0) / 100;

    if (valor <= 0 || assinaturaMes <= 0 || meses <= 0) return null;

    // 1. Custo Assinatura
    const totalAssinatura = assinaturaMes * meses;

    // 2. Custos Compra à Vista
    const valorFinalVeiculo = valor * Math.pow(1 - depAno, anos);
    const perdaDepreciacao = valor - valorFinalVeiculo;

    // IPVA (~4% a.a.) + Seguro (~3.5% a.a.) + Revisões/Pneus/Documentação (~1.5% a.a.) = ~9% a.a. sobre valor médio
    const valorMedio = (valor + valorFinalVeiculo) / 2;
    const ipvaSeguroManutencao = valorMedio * 0.09 * anos;

    // Custo de oportunidade líquido (CDI com IR de 15% para > 2 anos)
    const cdiLiquidoAno = cdiAno * 0.85;
    const rendimentoPerdido = valor * (Math.pow(1 + cdiLiquidoAno, anos) - 1);

    const custoTotalCompraVista = perdaDepreciacao + ipvaSeguroManutencao + rendimentoPerdido;
    const custoMensalCompraVista = custoTotalCompraVista / meses;

    const diferenca = custoTotalCompraVista - totalAssinatura;
    const assinaturaCompensa = diferenca > 0;

    return {
      totalAssinatura,
      assinaturaMes,
      perdaDepreciacao,
      ipvaSeguroManutencao,
      rendimentoPerdido,
      custoTotalCompraVista,
      custoMensalCompraVista,
      diferenca: Math.abs(diferenca),
      assinaturaCompensa
    };
  };

  const resultado = calcularComparativo();

  const formatBRL = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">
        Calculadora: Carro por Assinatura vs. Compra à Vista
      </h3>
      <p className="text-gray-600 text-sm mb-6">
        Descubra na ponta do lápis se compensa mais assinar um carro zero km ou comprar à vista (incluindo IPVA, seguro, depreciação e o rendimento do seu dinheiro no CDI).
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor do carro zero km (R$)
              <input
                type="number"
                value={valorCarro}
                onChange={(e) => setValorCarro(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                placeholder="140000"
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mensalidade da assinatura (R$/mês)
              <input
                type="number"
                value={mensalidadeAssinatura}
                onChange={(e) => setMensalidadeAssinatura(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                placeholder="3290"
              />
            </label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prazo
                <select
                  value={prazoMeses}
                  onChange={(e) => setPrazoMeses(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                >
                  <option value="12">12 meses</option>
                  <option value="24">24 meses</option>
                  <option value="36">36 meses</option>
                  <option value="48">48 meses</option>
                </select>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CDI anual (%)
                <input
                  type="number"
                  step="0.5"
                  value={taxaCdiAnual}
                  onChange={(e) => setTaxaCdiAnual(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                />
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Depreciação/ano (%)
                <input
                  type="number"
                  step="1"
                  value={depreciacaoAnual}
                  onChange={(e) => setDepreciacaoAnual(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-1"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Resultado em {prazoMeses} meses</h4>
          {resultado ? (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Depreciação estimada:</span>
                <span className="font-medium text-red-600">-{formatBRL(resultado.perdaDepreciacao)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">IPVA + Seguro + Revisões (~9%/ano):</span>
                <span className="font-medium text-red-600">-{formatBRL(resultado.ipvaSeguroManutencao)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Rendimento líquido perdido (CDI):</span>
                <span className="font-medium text-amber-600">-{formatBRL(resultado.rendimentoPerdido)}</span>
              </div>

              <div className="border-t border-gray-200 pt-3 flex justify-between">
                <span className="font-semibold text-gray-800">Custo Real da Compra:</span>
                <span className="font-bold text-gray-900">
                  {formatBRL(resultado.custoTotalCompraVista)} ({formatBRL(resultado.custoMensalCompraVista)}/mês)
                </span>
              </div>

              <div className="flex justify-between">
                <span className="font-semibold text-gray-800">Custo Total da Assinatura:</span>
                <span className="font-bold text-indigo-600">
                  {formatBRL(resultado.totalAssinatura)} ({formatBRL(resultado.assinaturaMes)}/mês)
                </span>
              </div>

              <div
                className={`mt-4 p-4 rounded-lg border ${
                  resultado.assinaturaCompensa
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-blue-50 border-blue-200 text-blue-900'
                }`}
              >
                <p className="font-bold text-base">
                  {resultado.assinaturaCompensa
                    ? `✅ Assinar economiza ${formatBRL(resultado.diferenca)} no período!`
                    : `💡 Comprar à vista economiza ${formatBRL(resultado.diferenca)} no período!`}
                </p>
                <p className="text-xs mt-1 opacity-90">
                  {resultado.assinaturaCompensa
                    ? 'Mantendo o valor do carro investido no CDI, os juros cobrem boa parte da mensalidade da assinatura.'
                    : 'Para esta mensalidade, comprar à vista sai mais barato — ou negocie uma assinatura menor.'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Preencha os campos para comparar os cenários.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarroAssinatura;
